import { Accordion, Badge, Button, ButtonGroup, Dropdown, DropdownButton, Form, Modal, Spinner } from "react-bootstrap";
import { accordionBodyClass } from "../../pages/Settings";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AppState } from "../../lib/context/AppProvider";
import { toast } from "react-toastify";
import { GROUP_SECTION_CLASS } from "./SettingsGroup";
import { FaRegTrashAlt } from "react-icons/fa";
import { isValidEmail, TooltipButton, vowelize } from "../../utils/general";
import { MdPersonRemove } from "react-icons/md";
import { PiNotePencil } from "react-icons/pi";
import { BiExit } from "react-icons/bi";
import InfoTooltip from "../common/InfoTooltip";

const CreateGroupModal = ({show, onHide, setGroupsDirty}) => {

    const {postData} = useContext(AppState);

    const [groupName, setGroupName] = useState(null);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if(show) {
            setGroupName(null);
            setCreating(false);
            setError(false);
        }
    }, [show]);

    const createGroup = async () => {
        setCreating(true);

        const response = await postData("user-groups/create", [], "POST", {
            name: groupName
        }, true);

        setCreating(false);

        if(response?.isError) {
            setError((await response.response.json()).message);
        }
        else if(response) {
            toast.success(`Group ${groupName} created`);
            onHide();
            setGroupsDirty(true);
        }
    };

    const valid = useMemo(() => groupName && !creating, [groupName, creating]);

    return (
        <Modal show={show} onHide={onHide} size="lg"
            className="customscrollbar"
            style={{
                maxHeight: "calc(100vh - 28px - 28px)",
                overflowY: "scroll"
            }}
        >
            <Modal.Header closeButton data-bs-theme="dark" className="bg-dark text-white">
                <Modal.Title>
                    Create User Group
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-dark text-white">
                <Form
                    onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if(valid) {
                            createGroup();
                        }
                    }}
                >
                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="group-name">Group Name</Form.Label>
                        <Form.Control
                            autoFocus
                            id="group-name"
                            type="text"
                            value={groupName ?? ""}
                            onChange={(e) => setGroupName(e.target.value)}
                            className="bg-dark text-white"
                            aria-label="Group Name"
                        />
                    </Form.Group>
                </Form>

                {error && (<p style={{color: "red"}}>{error}</p>)}
                
            </Modal.Body>
            <Modal.Footer className="bg-dark text-white">
                <Button variant="secondary" onClick={onHide} data-testid="group-create-close">
                    Close
                </Button>
                <Button 
                    data-testid="group-create"
                    variant="primary"
                    onClick={() => createGroup()}
                    disabled={!valid}
                >
                    Create {creating && (<Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                    />)}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const USER_GROUP_INVITABLE_ROLES = ["Manager", "Editor", "Member"];
const InviteModal = ({group, onHide, setGroupsDirty}) => {

    const {postData} = useContext(AppState);

    const [email, setEmail] = useState(null);
    const [role, setRole] = useState("Member");

    const [working, setWorking] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if(group) {
            setEmail(null);
            setRole("Member");
            setWorking(false);
            setError(false);
        }
    }, [group]);

    const inviteUser = async () => {
        setWorking(true);

        const response = await postData("user-groups/invite", [], "POST", {
            id: group.Id,
            email,
            role: role.toLowerCase()
        }, true);

        setWorking(false);

        if(response?.isError) {
            setError((await response.response.json()).message);
        }
        else if(response) {
            toast.success(`Invited ${email} to group as ${vowelize(role.toLowerCase())}`);
            onHide();
            setGroupsDirty(true);
        }
    };

    const valid = useMemo(() => group && email && isValidEmail(email) && !working, [group, email, working]);

    return (
        <Modal show={group} onHide={onHide} size="lg"
            className="customscrollbar"
            style={{
                maxHeight: "calc(100vh - 28px - 28px)",
                overflowY: "scroll"
            }}
        >
            <Modal.Header closeButton data-bs-theme="dark" className="bg-dark text-white">
                <Modal.Title>
                    Invite User
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-dark text-white">
                <Form
                    onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if(valid) {
                            inviteUser();
                        }
                    }}
                >
                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="email">Email Address</Form.Label>
                        <Form.Control
                            autoFocus
                            id="email"
                            type="text"
                            value={email ?? ""}
                            onChange={(e) => setEmail(e.target.value)}
                            className="bg-dark text-white"
                            isInvalid={email && (!isValidEmail(email))}
                        />
                    </Form.Group>

                    <div style={{
                        minHeight: "40px",
                        display: "flex",
                        alignItems: "center",
                        gap: "16px"
                    }}>
                        <div>
                            <div className="d-flex align-items-center">
                                <p className="text-light mb-0">Role:</p>
                                <InfoTooltip text="User will obtain the selected role when they accept the invitation. Managers can invite and remove users as well as change their roles, while editors can only modify constellations." />
                            </div>
                        </div>
                        <div style={{height: "38px"}}>
                            <DropdownButton
                                data-bs-theme="dark"
                                as={ButtonGroup}
                                title={role}
                                id="bg-nested-dropdown"
                                variant="dark"
                            >
                                {USER_GROUP_INVITABLE_ROLES.map((roleOpt) => (
                                    <Dropdown.Item
                                        eventKey={roleOpt}
                                        onClick={() => setRole(roleOpt)}
                                        key={`dropdown-groupinvite-${roleOpt}`}
                                        active={roleOpt === role}
                                    >
                                        {roleOpt}
                                    </Dropdown.Item>
                                ))}
                            </DropdownButton>
                        </div>
                    </div>
                </Form>

                {error && (<p style={{color: "red"}}>{error}</p>)}
                
            </Modal.Body>
            <Modal.Footer className="bg-dark text-white">
                <Button variant="secondary" onClick={onHide} data-testid="group-create-close">
                    Close
                </Button>
                <Button 
                    data-testid="group-create"
                    variant="primary"
                    onClick={() => inviteUser()}
                    disabled={!valid}
                >
                    Invite {working && (<Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                    />)}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const ChangeRoleModal = ({group, member, onHide, setGroupsDirty}) => {

    const {postData} = useContext(AppState);

    const [role, setRole] = useState("Member");

    const [working, setWorking] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if(member) {
            setRole(member.role[0].toUpperCase() + member.role.slice(1));
            setWorking(false);
            setError(false);
        }
    }, [member]);

    const changeRole = async () => {
        setWorking(true);

        const response = await postData("user-groups/role", [], "POST", {
            id: group.Id,
            uuid: member.uuid,
            role: role.toLowerCase()
        }, true);

        setWorking(false);

        if(response?.isError) {
            setError((await response.response.json()).message);
        }
        else if(response) {
            toast.success(`Changed ${member.name} role from ${member.role} to ${role.toLowerCase()}`);
            onHide();
            setGroupsDirty(true);
        }
    };

    const valid = useMemo(() => group && member && role.toLowerCase() !== member.role && !working, [group, member, role, working]);

    return (
        <Modal show={group} onHide={onHide} size="lg"
            className="customscrollbar"
            style={{
                maxHeight: "calc(100vh - 28px - 28px)",
                overflowY: "scroll"
            }}
        >
            <Modal.Header closeButton data-bs-theme="dark" className="bg-dark text-white">
                <Modal.Title>
                    Change User Role
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-dark text-white">
                <Form>
                    {member && (<p>{member.name}&apos;s current role: <UserRoleBadge role={member.role} /></p>)}
                    <div style={{
                        minHeight: "40px",
                        display: "flex",
                        alignItems: "center",
                        gap: "16px"
                    }}>
                        <div>
                            <div className="d-flex align-items-center">
                                <p className="text-light mb-0">New Role:</p>
                                <InfoTooltip text="Managers can invite and remove users as well as change their roles, while editors can only modify constellations." />
                            </div>
                        </div>
                        <div style={{height: "38px"}}>
                            <DropdownButton
                                data-bs-theme="dark"
                                as={ButtonGroup}
                                title={role}
                                id="bg-nested-dropdown"
                                variant="dark"
                            >
                                {USER_GROUP_INVITABLE_ROLES.map((roleOpt) => (
                                    <Dropdown.Item
                                        eventKey={roleOpt}
                                        onClick={() => setRole(roleOpt)}
                                        key={`dropdown-grouprole-${roleOpt}`}
                                        active={roleOpt === role}
                                    >
                                        {roleOpt}
                                    </Dropdown.Item>
                                ))}
                            </DropdownButton>
                        </div>
                    </div>
                </Form>

                {error && (<p style={{color: "red"}}>{error}</p>)}
                
            </Modal.Body>
            <Modal.Footer className="bg-dark text-white">
                <Button variant="secondary" onClick={onHide} data-testid="group-create-close">
                    Close
                </Button>
                <Button 
                    data-testid="group-create"
                    variant="primary"
                    onClick={() => changeRole()}
                    disabled={!valid}
                >
                    Update {working && (<Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                    />)}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const postGroupAction = async (postData, action, params, successMsg) => {
    const response = await postData("user-groups/"+action, [], "POST", params, true);

    if(response?.isError) {
        const msg = (await response.response.json()).message;
        toast.error(msg);
    }
    else if(response && successMsg){
        toast.success(successMsg);
    }
    return true;
};

const deleteGroup = async (postData, group) => {
    return await postGroupAction(postData, "delete", {
        id: group.Id,
    }, `Successfully deleted ${group.Name} group`);
};

const LeaveGroupButton = ({openConfirmModal, group, postData}) => {
    return <TooltipButton 
        tooltip="Leave Group"
        props={{
            variant: "danger",
            size: "sm",
            onClick: () => {
                openConfirmModal(
                    (<>leave the <strong>{group.Name}</strong> group</>), 
                    async () => await postGroupAction(postData, "leave", {
                        id: group.Id
                    }, `Successfully left ${group.Name}`),
                    "Leave"
                );
            }
        }}
    >
        <BiExit />
    </TooltipButton>;
}

const GroupButtonRow = ({
    group, isInvited, isManager, isOwner, 
    openConfirmModal, postData, setShowInviteModal, 
    setGroupsDirty
}) => {
    const userButton = isInvited ? 
        <TooltipButton 
            tooltip="Decline Invitation"
            props={{
                variant: "danger",
                size: "sm",
                onClick: () => {
                    openConfirmModal(
                        (<>decline your invitation to the <strong>{group.Name}</strong> group</>), 
                        async () => await postGroupAction(postData, "leave", {
                            id: group.Id
                        }, `Successfully declined ${group.Name} invitation`),
                        "Decline"
                    );
                }
            }}
        >
            <BiExit />
        </TooltipButton>
        : <LeaveGroupButton openConfirmModal={openConfirmModal} group={group} postData={postData} />;

    const [joining, setJoining] = useState(false);

    return (
        <div className="d-flex justify-content-between align-items-center mb-2">
            {isInvited && 
                <Button 
                    variant="outline-warning"
                    size="sm"
                    onClick={async () => {
                        setJoining(true);
                        await postGroupAction(postData, "join", {
                            id: group.Id
                        }, `Successfully joined ${group.Name}`);
                        setGroupsDirty(true);
                        setJoining(false);
                    }}
                    style={{ flexShrink: 0 }}
                    disabled={joining}
                >
                    Accept Invitation {joining && (<Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                    />)}
                </Button>
            }
            {isManager ? 
                <Button 
                    variant="info"
                    size="sm"
                    onClick={() => setShowInviteModal(group)}
                >
                    Invite Members
                </Button>
                : <div></div>
            }
            {isOwner ? 
                <TooltipButton 
                    tooltip="Delete Group"
                    props={{
                        variant: "danger",
                        size: "sm",
                        onClick: () => {
                            openConfirmModal(
                                (<>delete the <strong>{group.Name}</strong> group</>), 
                                async () => await deleteGroup(postData, group)
                            );
                        }
                    }}
                >
                    <FaRegTrashAlt />
                </TooltipButton>
                : 
                userButton
            }
        </div>
    );
};

const getBadgeColor = (role) => {
    switch(role) {
        case "owner":
            return "warning";
        case "manager":
            return "danger";
        case "editor":
            return "success";
        default:
            return "info";
    }
}

const UserRoleBadge = ({role}) => {
    return <Badge 
        bg={getBadgeColor(role)}
        className={getBadgeColor(role) === 'warning' ? 'text-dark ms-2' : 'ms-2'}
        style={{cursor: "default"}}
    >
        {role.toUpperCase()}
    </Badge>;
};

const ConfirmationModal = ({options, onHide, setGroupsDirty}) => {
    const [working, setWorking] = useState(false);
    const [shownOptions, setShownOptions] = useState(null);
    
    useEffect(() => {
        if(options) {
            setShownOptions(options);
        }
    }, [options]);

    return shownOptions && (
        <Modal 
            show={options} 
            onHide={onHide}
            size="md"
        >
            <Modal.Header closeButton data-bs-theme="dark" className="bg-dark text-white">
                <Modal.Title>Confirm</Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-dark text-white">
                Are you sure you want to {shownOptions.actionText}?
            </Modal.Body>
            <Modal.Footer className="bg-dark text-white">
                <Button 
                    variant="secondary" 
                    onClick={onHide}
                >
                    Cancel
                </Button>
                <Button 
                    variant="danger"
                    onClick={async () => {
                        setWorking(true);
                        if(await shownOptions.onConfirm()) {
                            setGroupsDirty(true);
                        }
                        setWorking(false);
                        onHide();
                    }}
                    disabled={working}
                >
                    {shownOptions.action} {working && (<Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                    />)}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

const MemberButtonRow = ({group, member, openConfirmModal, setShowChangeRoleModal}) => {
    const {postData} = useContext(AppState);

    if(member.status === "invitee") {
        return <TooltipButton 
            tooltip="Rescind Invite"
            props={{
                variant: "danger",
                size: "sm",
                onClick: () => {
                    openConfirmModal(
                        (<>rescind the group invitation for <strong>{member.name}</strong></>), 
                        async () => await postGroupAction(postData, "revoke", {
                            id: group.Id,
                            email: member.name
                        }, `Rescinded group invitation for ${member.name}`),
                        "Rescind"
                    );
                }
            }}
        >
            <MdPersonRemove />
        </TooltipButton>;
    }

    return <>
        <TooltipButton 
            tooltip="Change User Role"
            props={{
                variant: "warning",
                size: "sm",
                className: "me-2",
                onClick: () => {
                    setShowChangeRoleModal({group, member});
                }
            }}
        >
            <PiNotePencil />
        </TooltipButton>
        <TooltipButton 
            tooltip="Remove User"
            props={{
                variant: "danger",
                size: "sm",
                onClick: () => {
                    openConfirmModal(
                        (<>remove <strong>{member.name}</strong> from the group</>), 
                        async () => await postGroupAction(postData, "kick", {
                            id: group.Id,
                            uuid: member.uuid
                        }, `Removed ${member.name} from the group`),
                        "Remove"
                    );
                }
            }}
        >
            <MdPersonRemove />
        </TooltipButton>
    </>
};

const MemberItem = ({group, user, member, isManager, openConfirmModal, setShowChangeRoleModal}) => {
    return (
        <div className="d-flex justify-content-between align-items-center settings-user-row">
            <div className="small text-white ps-2">
                {member.name}
                <UserRoleBadge role={member.role} />
                {member.status === "invitee" && 
                    <Badge 
                        bg={"secondary"}
                        className='ms-2'
                        style={{cursor: "default"}}
                    >
                        INVITED
                    </Badge>
                }
            </div>
            <div>
                {isManager && member.role !== "owner" && member.uuid !== user.uuid && 
                    <MemberButtonRow group={group} member={member} openConfirmModal={openConfirmModal} setShowChangeRoleModal={setShowChangeRoleModal} />
                }
            </div>
        </div>
    );
};

const weight = (member) => {
    let score = 0;

    switch(member.role) {
        case "owner":
            score += 1000;
            break;
        case "manager":
            score += 200;
            break;
        case "editor":
            score += 100;
            break;
        default: 
            break;
    }

    if (member.status === "member") {
        score += 10000;
    }

    return score;
};

const GroupItem = ({group, user, openConfirmModal, postData, setShowInviteModal, setGroupsDirty, setShowChangeRoleModal}) => {
    const [members, setMembers] = useState([]);
    useEffect(() => {
        const list = Object.keys(group.Members).map((uuid) => ({
            ...group.Members[uuid],
            uuid
        }));

        list.sort((a, b) => weight(b) - weight(a));

        setMembers(list);
    }, [group]);

    const self = group.Members[user.uuid] ?? group.Members[user.email];
    const isInvited = self.status === "invitee";
    const isOwner = !isInvited && self.role === "owner";
    const isManager = isOwner || (!isInvited && self.role === "manager");

    return (
        <Accordion.Item eventKey={group.Id+""} className="bg-dark border-0 mt-3">
            <Accordion.Header className="settings-accordion-header">
                <h4 className="settings-section-title m-0">
                    {group.Name}{isInvited ? " (invited)" : ""}
                </h4>
            </Accordion.Header>
            <Accordion.Body className={accordionBodyClass} style={{overflowY: "clip"}}>
                <div 
                    className="p-3 mb-2 rounded"
                    style={{ backgroundColor: "#1a1d20" }}
                >
                    {(isManager || isInvited) && <GroupButtonRow
                        group={group}
                        isInvited={isInvited}
                        isManager={isManager}
                        isOwner={isOwner}
                        openConfirmModal={openConfirmModal}
                        postData={postData}
                        setShowInviteModal={setShowInviteModal}
                        setGroupsDirty={setGroupsDirty}
                    />}
                    <div className={GROUP_SECTION_CLASS}>
                        <div><small className="text-light">Group members:</small></div>
                        {!(isManager || isInvited) && <LeaveGroupButton openConfirmModal={openConfirmModal} group={group} postData={postData} />}
                    </div>
                    <div className="settings-group-list customscrollbar" style={{maxHeight: `${120+16}px`}}>
                        {members.map(member => 
                            <MemberItem 
                                key={member.name}
                                group={group} 
                                user={user} 
                                member={member}
                                isManager={isManager}
                                isOwner={isOwner}
                                openConfirmModal={openConfirmModal}
                                setShowChangeRoleModal={setShowChangeRoleModal}
                            />
                        )}
                    </div>
                </div>
            </Accordion.Body>
        </Accordion.Item>
    );
};

const GroupSettings = () => {
    const { user, getData, postData } = useContext(AppState);

    const [groups, setGroups] = useState([]);
    const [groupsDirty, setGroupsDirty] = useState(false);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showChangeRoleModal, setShowChangeRoleModal] = useState(false);
    const [confirmModalOptions, setConfirmModalOptions] = useState(false);

    const [activeKeys, setActiveKeys] = useState([]);
    
    useEffect(() => {
        if(window.location.hash?.length > 1) {
            const hash = window.location.hash.substring(1);
            const key = hash.split("/")[1];
            if(key) {
                setActiveKeys([key+""]);
            }
        }
    }, []);

    const openConfirmModal = useCallback((actionText, onConfirm, action="Delete") => {
        setConfirmModalOptions({actionText, onConfirm, action: action})
    }, []);

    const fetchData = useCallback(async () => {
        if(user.isGuest) return;

        const groupsData = await getData("user-groups");

        if(groupsData) {
            setGroups([...groupsData.groups, ...groupsData.invited]);
        }
    }, [user, getData]);
    
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if(groupsDirty) {
            fetchData();
            setGroupsDirty(false);
        }
    }, [groupsDirty, fetchData]);

    return (
        <Accordion.Item eventKey="groups" className="bg-dark border-0 mt-3">
            <Accordion.Header className="settings-accordion-header">
                <h4 className="settings-section-title m-0">GROUPS</h4>
            </Accordion.Header>
            <Accordion.Body className={accordionBodyClass + " customscrollbar"} style={{maxHeight: "50vh"}}>
                <div className="d-flex justify-content-between align-items-center">
                    <p className="settings-subsection-title-left">
                        Group-level management of constellation alerts.
                    </p>
                    <Button 
                        variant="primary"
                        onClick={() => setShowCreateModal(true)}
                        className="mb-3"
                        style={{ flexShrink: 0 }}
                    >
                        + Create Group
                    </Button>
                </div>

                <Accordion 
                    className="mb-3 settings-accordion"
                    activeKey={activeKeys}
                    onSelect={(key) => {
                        setActiveKeys(prevKeys => 
                            prevKeys.includes(key) ? [] : [key]
                        );
                    }}
                >
                    {groups.map((group) => {
                        return (
                            <GroupItem 
                                key={group.Id} group={group} user={user} 
                                openConfirmModal={openConfirmModal}
                                postData={postData}
                                setShowInviteModal={setShowInviteModal}
                                setGroupsDirty={setGroupsDirty}
                                setShowChangeRoleModal={setShowChangeRoleModal}
                            />
                        );
                    })}
                </Accordion>
            </Accordion.Body>

            <CreateGroupModal 
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                setGroupsDirty={setGroupsDirty}
            />

            <InviteModal 
                group={showInviteModal}
                onHide={() => setShowInviteModal(null)}
                setGroupsDirty={setGroupsDirty}
            />

            <ChangeRoleModal 
                group={showChangeRoleModal?.group}
                member={showChangeRoleModal?.member}
                onHide={() => setShowChangeRoleModal(null)}
                setGroupsDirty={setGroupsDirty}
            />

            <ConfirmationModal
                options={confirmModalOptions}
                onHide={() => setConfirmModalOptions(null)}
                setGroupsDirty={setGroupsDirty}
            />
        </Accordion.Item>
    );
};

export default GroupSettings;